import { useCallback, useEffect, useState } from "react";
import { api, errMsg, download, itDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Link } from "react-router-dom";

import { Plus, Printer, CheckCircle, Trash } from "@phosphor-icons/react";

const MESI_IT = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
                 "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const ultimoGiorno = (y, m) => new Date(y, m, 0).getDate();


const now = () => new Date();
const hhmm = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

// I verbali importati dal vecchio gestionale possono avere le verifiche in formati diversi
// (elenco di testi, oggetto voce/esito): qui diventano sempre [{ subject, result }].
const normalizeChecks = (checks) => {
  if (Array.isArray(checks)) {
    return checks.map((c) =>
      c && typeof c === "object"
        ? { subject: String(c.subject ?? ""), result: String(c.result ?? "") }
        : { subject: String(c ?? ""), result: "" });
  }
  if (checks && typeof checks === "object") {
    return Object.entries(checks).map(([subject, result]) => ({ subject, result: String(result ?? "") }));
  }
  return [];
};

export default function SupervisorRegister() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [config, setConfig] = useState({ checks: [], results: [] });
  const [departments, setDepartments] = useState([]);
  const [signed, setSigned] = useState("all");
  const [month, setMonth] = useState("all");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [form, setForm] = useState(null);
  const [preposti, setPreposti] = useState([]);
  const canDelete = ["admin", "manager"].includes(user?.role);
  // il verbale può avere un preposto storico non più designato: resta selezionabile
  const opzioniPreposto = form?.supervisor && !preposti.includes(form.supervisor)
    ? [form.supervisor, ...preposti] : preposti;

  const load = useCallback(async () => {
    const params = { signed };
    if (month !== "all") {
      const m = Number(month), y = Number(year);
      params.date_from = `${y}-${String(m).padStart(2, "0")}-01`;
      params.date_to = `${y}-${String(m).padStart(2, "0")}-${ultimoGiorno(y, m)}`;
    }
    try { setRows((await api.get("/safety/inspections", { params })).data); }
    catch (e) { toast.error(errMsg(e)); }
  }, [signed, month, year]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/safety/inspections/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/employees", { params: { status: "active" } })
      .then((r) => setPreposti((r.data || []).filter((e) => e.is_preposto)
        .map((e) => `${e.last_name || ""} ${e.first_name || ""}`.trim())))
      .catch(() => {});
  }, []);

  const newForm = () => setForm({
    date: now().toISOString().slice(0, 10), time: hhmm(now()),
    supervisor: preposti.length === 1 ? preposti[0] : "", department: "", nc_description: "", worker_recalls: "",
    checks: (config.checks || []).map((s) => ({ subject: s, result: "Esito POSITIVO" })),
  });

  const save = async () => {
    try {
      const body = {
        date: form.date, time: form.time, supervisor: form.supervisor, department: form.department,
        checks: form.checks, nc_description: form.nc_description || "", worker_recalls: form.worker_recalls || "",
      };
      const r = form.id
        ? await api.put(`/safety/inspections/${form.id}`, body)
        : await api.post("/safety/inspections", body);
      toast.success(`Verbale n. ${r.data.progressive} salvato`);
      setForm(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const print = (d) =>
    download(`/safety/inspections/${d.id}/pdf`, `registro_preposto_${d.progressive}.pdf`)
      .catch((e) => toast.error(errMsg(e)));

  const printUnsigned = () =>
    download("/safety/inspections/print-unsigned", "registro_preposto_da_firmare.pdf")
      .catch((e) => toast.error(errMsg(e)));

  const remove = async (d) => {
    if (!window.confirm(`Eliminare definitivamente il verbale n. ${d.progressive} del ${itDate(d.date)}?`)) return;
    try {
      await api.delete(`/safety/inspections/${d.id}`);
      toast.success(`Verbale n. ${d.progressive} eliminato`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-3" data-testid="safety-log-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Registro Preposto</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Registro dell'attività di vigilanza ai sensi dell'art. 19 D.Lgs. 81/08: compila le verifiche,
            fai firmare il verbale e archivialo.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="rounded-sm font-semibold"
            data-testid="inspection-print-unsigned" onClick={printUnsigned}>
            <Printer size={15} className="mr-1.5" /> Stampa DA FIRMARE
          </Button>
          <Button size="sm" className="rounded-sm font-semibold" data-testid="inspection-add" onClick={newForm}>
            <Plus size={15} className="mr-1.5" /> Nuova verifica
          </Button>
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card p-3 mobile-stack flex flex-wrap gap-2 items-center">
        <Select value={signed} onValueChange={setSigned}>
          <SelectTrigger className="h-9 w-[200px] rounded-sm" data-testid="inspection-signed-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i verbali</SelectItem>
            <SelectItem value="no">Da firmare</SelectItem>
            <SelectItem value="yes">Firmati</SelectItem>
          </SelectContent>
        </Select>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="h-9 w-[160px] rounded-sm" data-testid="inspection-month-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i mesi</SelectItem>
            {MESI_IT.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={year} onValueChange={setYear} disabled={month === "all"}>
          <SelectTrigger className="h-9 w-[110px] rounded-sm" data-testid="inspection-year-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[0, 1, 2, 3].map((k) => {
              const y = String(new Date().getFullYear() - k);
              return <SelectItem key={y} value={y}>{y}</SelectItem>;
            })}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground" data-testid="inspection-count">
          {rows.length} verbali
        </span>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
        <table className="w-full text-sm" data-testid="inspections-table">
          <thead className="bg-secondary"><tr>
            {[["", 230], ["ID", 56], ["Data", 96], ["Ora", 64], ["Preposto", 180], ["Reparto", 200],
              ["Esito", 130], ["Firmato", 110]].map(([h, w]) => (
              <th key={h} style={{ width: w }}
                className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id} className="border-t border-border hover:bg-secondary/40" data-testid={`inspection-row-${d.progressive}`}>
                <td className="px-3 py-1.5 text-left">
                  <div className="flex items-center justify-start gap-3 whitespace-nowrap">
                    <button type="button" className="text-xs font-semibold hover:underline px-1 py-1" data-testid={`inspection-open-${d.progressive}`}
                      onClick={() => setForm({ ...d, checks: normalizeChecks(d.checks) })}>VERIFICHE</button>
                    <button type="button" className="text-muted-foreground hover:text-primary p-1" title="Stampa verbale"
                      data-testid={`inspection-print-${d.progressive}`} onClick={() => print(d)}>
                      <Printer size={16} />
                    </button>
                    {!d.signed && (
                      <button type="button" className="text-xs font-semibold text-emerald-600 hover:underline px-1 py-1"
                        data-testid={`inspection-sign-${d.progressive}`}
                        onClick={async () => {
                          try { await api.patch(`/safety/inspections/${d.id}`, { signed: true }); toast.success("Verbale firmato e archiviato"); load(); }
                          catch (e) { toast.error(errMsg(e)); }
                        }}>Firma</button>
                    )}
                    {canDelete && (
                      <button type="button" className="text-rose-600 hover:text-rose-700 p-1" title="Elimina verbale"
                        data-testid={`inspection-delete-${d.progressive}`} onClick={() => remove(d)}>
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 py-1.5 font-semibold tabular-nums">{d.progressive}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{itDate(d.date)}</td>
                <td className="px-3 py-1.5">{d.time}</td>
                <td className="px-3 py-1.5">{d.supervisor}</td>
                <td className="px-3 py-1.5">{d.department || "—"}</td>
                <td className="px-3 py-1.5">
                  {d.has_nc
                    ? <span className="text-xs font-semibold text-rose-600">NON CONFORMITÀ</span>
                    : <span className="text-xs font-semibold text-emerald-600">CONFORME</span>}
                </td>
                <td className="px-3 py-1.5">
                  {d.signed
                    ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold"><CheckCircle size={14} weight="fill" /> {itDate(d.signed_date)}</span>
                    : <span className="text-xs text-amber-600 font-semibold">DA FIRMARE</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground" data-testid="inspections-empty">Nessuna verifica registrata</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {form && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-sm sm:max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="inspection-dialog">
            <DialogHeader>
              <DialogTitle className="font-head">
                {form.id ? `Verifiche — verbale n. ${form.progressive}` : "Nuova verifica del preposto"}
              </DialogTitle>
            </DialogHeader>

            <div className="grid sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Data</Label>
                <Input type="date" value={form.date ?? ""} onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="h-9 rounded-sm mt-1" data-testid="insp-date" />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ora</Label>
                <Input value={form.time ?? ""} onChange={(e) => setForm({ ...form, time: e.target.value })}
                  className="h-9 rounded-sm mt-1" data-testid="insp-time" />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Preposto</Label>
                <Select value={form.supervisor || ""} onValueChange={(v) => setForm({ ...form, supervisor: v })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="insp-supervisor">
                    <SelectValue placeholder={preposti.length ? "Scegli il preposto" : "Nessun preposto designato"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-56">
                    {opzioniPreposto.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                {preposti.length === 0 && (
                  <p className="text-[10px] text-muted-foreground mt-1" data-testid="insp-no-preposti">
                    Designa i preposti in Gestione Sicurezza → <Link to="/preposti" className="underline font-semibold"
                      data-testid="insp-link-preposti">Preposti</Link>
                  </p>
                )}
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Reparto</Label>
                <Select value={form.department || ""} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="insp-department"><SelectValue placeholder="Reparto" /></SelectTrigger>
                  <SelectContent className="max-h-56">
                    {departments.map((dep) => <SelectItem key={dep} value={dep}>{dep}</SelectItem>)}
                    <SelectItem value="MACELLO/DISOSSO/SEZIONATO">MACELLO/DISOSSO/SEZIONATO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              {(form.checks || []).map((ck, idx) => (
                <div key={ck.subject || idx} className="grid sm:grid-cols-2 gap-2 items-center" data-testid={`insp-check-${idx}`}>
                  <div className="text-sm">{ck.subject}</div>
                  <Select value={ck.result || ""}
                    onValueChange={(v) => {
                      const checks = [...form.checks];
                      checks[idx] = { ...ck, result: v };
                      setForm({ ...form, checks });
                    }}>
                    <SelectTrigger className={`h-9 rounded-sm ${ck.result && ck.result !== "Esito POSITIVO" ? "border-rose-400 text-rose-700" : ""}`}
                      data-testid={`insp-result-${idx}`}><SelectValue placeholder="Esito" /></SelectTrigger>
                    <SelectContent>
                      {/* agli esiti previsti si aggiunge quello già registrato sul verbale, anche se
                          scritto diversamente nei dati storici, così non va perso salvando */}
                      {Array.from(new Set([...(config.results || []), ck.result].filter(Boolean)))
                        .map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Descrizione delle Non Conformità (NC) e azioni correttive immediate
                </Label>
                <Textarea value={form.nc_description ?? ""} onChange={(e) => setForm({ ...form, nc_description: e.target.value })}
                  className="rounded-sm mt-1 min-h-20" data-testid="insp-nc" />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Richiami ai lavoratori
                </Label>
                <Textarea value={form.worker_recalls ?? ""} onChange={(e) => setForm({ ...form, worker_recalls: e.target.value })}
                  className="rounded-sm mt-1 min-h-20" data-testid="insp-recalls" />
              </div>
              <p className="text-xs text-muted-foreground">
                Nota: con esito negativo l'attività lavorativa deve essere sospesa, a meno che la non conformità
                venga rimossa applicando immediatamente le necessarie azioni correttive.
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setForm(null)} data-testid="inspection-cancel">Annulla</Button>
              {form.id && (
                <Button variant="outline" className="rounded-sm" data-testid="inspection-print-dialog" onClick={() => print(form)}>
                  <Printer size={15} className="mr-1.5" /> Stampa verbale
                </Button>
              )}
              <Button className="rounded-sm font-semibold" data-testid="inspection-save" onClick={save}>Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
